import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  ChevronRight,
  Search,
  ClipboardCheck,
  Link2,
  Check,
  X,
  Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  PageHeader,
  MetricCard,
} from '@/components/common';
import {
  rfqApi,
  FreightRFQ as FreightRFQType,
  RFQStatus,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusColors: Record<RFQStatus, string> = {
  Draft: 'bg-gray-100 text-gray-600 border-gray-200',
  Open: 'bg-blue-100 text-blue-600 border-blue-200',
  Closed: 'bg-amber-100 text-amber-600 border-amber-200',
  Awarded: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  Cancelled: 'bg-red-100 text-red-600 border-red-200',
};

const statusIcons: Record<RFQStatus, React.ReactNode> = {
  Draft: <FileText className="w-3.5 h-3.5" />,
  Open: <Send className="w-3.5 h-3.5" />,
  Closed: <Clock className="w-3.5 h-3.5" />,
  Awarded: <CheckCircle className="w-3.5 h-3.5" />,
  Cancelled: <XCircle className="w-3.5 h-3.5" />,
};

export default function FreightRFQPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rfqs, setRfqs] = useState<FreightRFQType[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  // URL-based filters
  const activeTab = searchParams.get('tab') || 'all';
  const searchQuery = searchParams.get('search') || '';

  const setActiveTab = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'all') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  const setSearchQuery = (search: string) => {
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = activeTab !== 'all' || searchQuery !== '';

  // Copy shareable link
  const handleCopyLink = useCallback(() => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      toast.success('Link copied! Share this URL to show others this filtered view.');
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  }, []);

  useEffect(() => {
    loadRFQs();
  }, []);

  const loadRFQs = async () => {
    try {
      setLoading(true);
      const response = await rfqApi.getAll();
      // Handle both paginated response { data: [] } and direct array response
      const rfqData = Array.isArray(response) ? response : (response?.data || []);
      setRfqs(rfqData);
    } catch (error) {
      console.error('Failed to load RFQs:', error);
      toast.error(getErrorMessage(error));
      setRfqs([]); // Ensure rfqs is always an array on error
    } finally {
      setLoading(false);
    }
  };

  const filteredRFQs = rfqs.filter((rfq) => {
    const matchesTab =
      activeTab === 'all' || rfq.status.toLowerCase() === activeTab;
    const matchesSearch =
      !searchQuery ||
      rfq.rfqNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rfq.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const counts = {
    total: rfqs.length,
    draft: rfqs.filter((r) => r.status === 'Draft').length,
    open: rfqs.filter((r) => r.status === 'Open').length,
    awarded: rfqs.filter((r) => r.status === 'Awarded').length,
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Freight RFQ"
          description="Rate procurement requests"
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Freight RFQ"
        description="Rate procurement requests"
        actions={
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                title="Copy shareable link with current filters"
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Share View
                  </>
                )}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/tms/rfq-approvals')}>
              <ClipboardCheck className="w-4 h-4 mr-2" />
              My Approvals
            </Button>
            <Button onClick={() => navigate('/tms/rfq/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Create RFQ
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total RFQs"
          value={counts.total}
          icon={<FileText className="w-5 h-5" />}
        />
        <MetricCard
          label="Draft"
          value={counts.draft}
          icon={<FileText className="w-5 h-5 text-gray-500" />}
        />
        <MetricCard
          label="Open"
          value={counts.open}
          icon={<Send className="w-5 h-5 text-blue-500" />}
        />
        <MetricCard
          label="Awarded"
          value={counts.awarded}
          icon={<CheckCircle className="w-5 h-5 text-emerald-500" />}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="all">
            All RFQs
            <Badge variant="secondary" className="ml-2">{rfqs.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="draft">
            Draft
            <Badge variant="secondary" className="ml-2">{counts.draft}</Badge>
          </TabsTrigger>
          <TabsTrigger value="open">
            Open
            <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-600">{counts.open}</Badge>
          </TabsTrigger>
          <TabsTrigger value="awarded">
            Awarded
            <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-600">{counts.awarded}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search & Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by RFQ number or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button variant="outline">
          <Filter className="w-4 h-4 mr-2" />
          Filters
        </Button>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-destructive hover:text-destructive"
          >
            <X className="w-4 h-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* RFQ List */}
      <div className="space-y-3">
        {filteredRFQs.map((rfq) => (
          <div
            key={rfq.id}
            onClick={() => navigate(`/tms/rfq/${rfq.id}`)}
            className="bg-card rounded-xl border p-4 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-muted-foreground">
                    {rfq.rfqNumber}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                      statusColors[rfq.status]
                    }`}
                  >
                    {statusIcons[rfq.status]}
                    {rfq.status}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {rfq.type}
                  </Badge>
                </div>
                <h3 className="font-medium text-foreground mt-1">{rfq.title}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span>
                    {rfq._count?.lines ?? rfq.lines?.length ?? 0} lane(s)
                  </span>
                  <span>•</span>
                  <span>
                    {rfq._count?.invitations ?? rfq.invitations?.length ?? 0}{' '}
                    carrier(s) invited
                  </span>
                  <span>•</span>
                  <span>
                    Due:{' '}
                    {new Date(rfq.responseDeadline).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>
        ))}

        {filteredRFQs.length === 0 && (
          <div className="text-center py-12 bg-card rounded-xl border">
            <FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No RFQs found</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/tms/rfq/new')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create your first RFQ
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
